import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack, CircularProgress } from "@mui/material";
import TopBar from "../../../../../../component/TopBar";
import ActiveBar from "../../../../../../component/PolicyNavbar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import SuccessDialog from "../../../../../../component/SuccessDialog";
import SectionLoader from "../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../component/EmptyState";
import { WizardCard, FormSection } from "../../../../../../component/wizard";
import { getLeavePolicyById, publishLeavePolicy, getAllLeaveType } from "../../../../../../services/leave";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../theme";

const Fact = ({ label, value }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mt: "0.25rem" }}>{value ?? "—"}</Box>
  </Box>
);
const Grid = ({ children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: "1.25rem" }}>{children}</Box>
);

const LeavePolicyReview = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const [policy, setPolicy] = useState(null);
  const [typeById, setTypeById] = useState({});
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(false);
  const policyId = state?.policyId;

  useEffect(() => {
    if (!policyId) { setLoading(false); return; }
    (async () => {
      try {
        const [p, types] = await Promise.all([getLeavePolicyById(policyId), getAllLeaveType().catch(() => null)]);
        setPolicy(p?.data);
        const m = {}; (types?.data || []).forEach((t) => { m[String(t._id)] = t.name; });
        setTypeById(m);
      } catch (e) {
        notify.error(e?.message || "Failed to load policy");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await publishLeavePolicy(policyId);
      if (res?.success === false) throw apiError(res, "Could not publish");
      notify.success(`Published — credited ${res?.data?.employees || 0} employee(s)`);
      setDone(true);
    } catch (e) { notify.error(e?.message || "Could not publish policy"); }
    finally { setPublishing(false); }
  };

  if (!policyId) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Leave Policy"} /><EmptyState title="No policy to review" /></Box>;

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Leave Policy"} breadcrumbs={[{ label: "Leave Policy", href: "/app/leave-policy" }, { label: "Review" }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <ActiveBar active={["Policy Details", "Assign Policy", "Rules", "Review"]} />
          <BtnSecondary to="/app/leave-policy" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        {loading ? <SectionLoader /> : !policy ? <EmptyState title="Policy not found" /> : (
          <>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", mb: "1.25rem", px: "0.75rem", py: "0.5rem", borderRadius: "999px", background: tokens.warningTint }}>
              <Box sx={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: tokens.warning }} />
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>Draft — balances aren't granted until published</Box>
            </Box>

            <FormSection title="Policy details" description="">
              <Grid>
                <Fact label="Name" value={policy.name} />
                <Fact label="Code" value={policy.code} />
                <Fact label="Period" value={`${policy.startDate ? new Date(policy.startDate).toLocaleDateString("en-GB") : "—"} → ${policy.endDate ? new Date(policy.endDate).toLocaleDateString("en-GB") : "—"}`} />
              </Grid>
            </FormSection>

            <FormSection title="Entitlements" description="">
              <Grid>
                {(policy.leaveQuantities || []).map((lq, i) => (
                  <Fact key={i} label={typeById[String(lq.leaveType)] || "Leave"} value={`${lq.quantity} day(s)`} />
                ))}
              </Grid>
            </FormSection>

            <FormSection title="Assignment & approval" description="">
              <Grid>
                <Fact label="Departments" value={`${(policy.departments || []).length}`} />
                <Fact label="Groups" value={`${(policy.groups || []).length}`} />
                <Fact label="Approvers" value={`${(policy.approvers || []).length}`} />
              </Grid>
            </FormSection>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "2rem" }}>
              <BtnSecondary to="/app/leave-policy" sx={{ borderRadius: "8px" }}>Save as draft</BtnSecondary>
              <BtnPrimary onClick={publish} disabled={publishing} sx={{ borderRadius: "8px" }}>
                {publishing ? <CircularProgress size={"1.25rem"} /> : "Publish policy"}
              </BtnPrimary>
            </Stack>
          </>
        )}

        <SuccessDialog open={done} onClose={() => setDone(false)} message={"Leave policy published!"}
          addNewLink={"/app/leave-policy/settings"} addNewText="Create New Policy"
          viewAllLink={"/app/leave-policy"} viewAllText="View All Policies" />
      </WizardCard>
    </Box>
  );
};

export default LeavePolicyReview;
